import React, { useState, useEffect, useMemo } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "../../../styles/charts/LeadSourcesChart.scss";
import CommonSelect from "../../../../../../core/common/commonSelect";

interface ChartDataPoint {
  name: string;
  total_leads: number;
  converted: number;
  lost: number;
  in_process: number;
  conversion_rate: number;
  hasChildren: boolean;
}

interface LeadSourcesChartProps {
  data: any;
  itemsPerPage?: number;
}

const LeadSourcesChart: React.FC<LeadSourcesChartProps> = ({
  data,
  itemsPerPage = 5,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPipeline, setSelectedPipeline] = useState<string>("-1");
  const [navigationStack, setNavigationStack] = useState<
    Array<{ sourceName: string; data: any[] }>
  >([]);

  const currentFilteredData = useMemo(() => {
    if (selectedPipeline === "-1") {
      return data.total;
    }
    return data.by_pipeline[selectedPipeline] || [];
  }, [data, selectedPipeline]);

  const currentLevelData = useMemo(() => {
    if (navigationStack.length === 0) {
      return currentFilteredData;
    }
    return navigationStack[navigationStack.length - 1].data;
  }, [currentFilteredData, navigationStack]);

  const chartData = useMemo((): ChartDataPoint[] => {
    return currentLevelData
      .map((item: any) => ({
        name: item.source,
        total_leads: item.total_leads || 0,
        converted: item.converted || 0,
        lost: item.lost || 0,
        in_process: item.in_process || 0,
        conversion_rate: item.conversion_rate || 0,
        hasChildren: item.children && item.children.length > 0,
      }))
      .filter((item: any) => item.total_leads > 0);
  }, [currentLevelData]);

  const totalPages = Math.ceil(chartData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPageData = chartData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const pipelineOptions =
    data?.pipelines?.map((pipeline: any) => ({
      value: pipeline?.id?.toString(),
      label: pipeline?.pipeline_name,
    })) || [];

  useEffect(() => {
    setCurrentPage(1);
    setNavigationStack([]);
  }, [selectedPipeline]);

  useEffect(() => {
    setCurrentPage(1);
  }, [chartData]);

  const handleSourceClick = (sourceName: string) => {
    const clickedSource = chartData.find((item) => item.name === sourceName);
    if (clickedSource && clickedSource.hasChildren) {
      const sourceData = currentLevelData.find(
        (item: any) => item.source === sourceName
      );
      if (sourceData && sourceData.children) {
        setNavigationStack((prev) => [
          ...prev,
          { sourceName: sourceName, data: sourceData.children },
        ]);
      }
    }
  };

  const handleNavigateBack = (index: number) => {
    if (index === 0) {
      setNavigationStack([]);
    } else {
      setNavigationStack((prev) => prev.slice(0, index));
    }
    setCurrentPage(1);
  };

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  const CustomXAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const text = payload.value;
    const dataPoint = currentPageData.find((d) => d.name === text);
    const isClickable = dataPoint?.hasChildren || false;
    const maxLength = 15;
    const maxLines = 3;
    const lines = [];

    if (text.length > maxLength) {
      const words = text.split(" ");

      if (words.length > 1) {
        let currentLine = "";

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;

          if (testLine.length <= maxLength) {
            currentLine = testLine;
          } else {
            if (currentLine) {
              lines.push(currentLine);
              if (lines.length >= maxLines - 1) {
                const remainingWords = words.slice(words.indexOf(word));
                const remainingText = remainingWords.join(" ");
                if (remainingText.length > maxLength) {
                  lines.push(remainingText.substring(0, maxLength - 3) + "...");
                } else {
                  lines.push(remainingText);
                }
                break;
              }
              currentLine = word;
            } else {
              if (word.length > maxLength) {
                lines.push(word.substring(0, maxLength - 3) + "...");
                break;
              } else {
                currentLine = word;
              }
            }
          }
        }

        if (currentLine && lines.length < maxLines) {
          lines.push(currentLine);
        }
      } else {
        const word = text;
        if (word.length > maxLength * maxLines) {
          lines.push(word.substring(0, maxLength - 3) + "...");
        } else if (word.length > maxLength * 2) {
          const partLength = Math.ceil(word.length / 3);
          lines.push(word.substring(0, partLength));
          lines.push(word.substring(partLength, partLength * 2));
          lines.push(word.substring(partLength * 2));
        } else if (word.length > maxLength) {
          const mid = Math.ceil(word.length / 2);
          lines.push(word.substring(0, mid));
          lines.push(word.substring(mid));
        } else {
          lines.push(word);
        }
      }
    } else {
      lines.push(text);
    }

    return (
      <g transform={`translate(${x},${y})`}>
        {lines.map((line, i) => (
          <text
            key={i}
            x={0}
            y={i * 14}
            fill={isClickable ? "#3b82f6" : "#6b7280"}
            textAnchor="middle"
            fontSize={11}
            dominantBaseline="hanging"
            style={{
              cursor: isClickable ? "pointer" : "default",
              fontWeight: isClickable ? "600" : "normal",
            }}
            onClick={() => {
              if (isClickable) {
                handleSourceClick(text);
              }
            }}>
            {line}
          </text>
        ))}
      </g>
    );
  };

  const CustomDot = (props: any) => {
    const { cx, cy, stroke, fill, payload } = props;
    const isClickable = payload?.hasChildren || false;

    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={fill}
        stroke={stroke}
        strokeWidth={2}
        style={{
          cursor: isClickable ? "pointer" : "default",
          opacity: isClickable ? 1 : 0.7,
        }}
        onClick={() => {
          if (isClickable) {
            handleSourceClick(payload.name);
          }
        }}
      />
    );
  };

  const CustomActiveDot = (props: any) => {
    const { cx, cy, stroke, fill, payload } = props;
    const isClickable = payload?.hasChildren || false;

    return (
      <g>
        {isClickable && (
          <circle
            cx={cx}
            cy={cy}
            r={12}
            fill="transparent"
            style={{ cursor: "pointer" }}
            onClick={() => handleSourceClick(payload.name)}
          />
        )}
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill={fill}
          stroke={stroke}
          strokeWidth={3}
          style={{
            cursor: isClickable ? "pointer" : "default",
            opacity: isClickable ? 1 : 0.7,
          }}
          onClick={() => {
            if (isClickable) {
              handleSourceClick(payload.name);
            }
          }}
        />
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;

      return (
        <div className="lead-sources-tooltip">
          <p className="tooltip-title">{label}</p>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot total-leads" />
              <span>Total</span>
            </div>
            <span className="tooltip-value">{data?.total_leads || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot converted" />
              <span>Converted</span>
            </div>
            <span className="tooltip-value">{data?.converted || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot in-process" />
              <span>In Process</span>
            </div>
            <span className="tooltip-value">{data?.in_process || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot lost" />
              <span>Lost</span>
            </div>
            <span className="tooltip-value">{data?.lost || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot conversion-rate" />
              <span>Conversion Rate</span>
            </div>
            <span className="tooltip-value">{data?.conversion_rate || 0}%</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="lead-sources-chart-container">
      <div className="d-flex gap-4 align-items-center justify-content-center">
        <div style={{ width: "200px" }}>
          <CommonSelect
            className="select small text-muted"
            options={[
              { value: "-1", label: "All Pipelines" },
              ...pipelineOptions,
            ]}
            defaultValue={
              selectedPipeline
                ? pipelineOptions.find(
                    (pipeline: any) => pipeline.value === selectedPipeline
                  )
                : undefined
            }
            onChange={(selected: any) => {
              setSelectedPipeline(selected.value);
            }}
            placeHolder={"Filter By Pipeline"}
          />
        </div>

        <div className="chart-legend">
          <div className="legend-item">
            <div className="legend-dot total-leads" />
            <span className="legend-text">Total</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot converted" />
            <span className="legend-text">Converted</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot in-process" />
            <span className="legend-text">In Process</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot lost" />
            <span className="legend-text">Lost</span>
          </div>
        </div>
      </div>

      <div className="chart-wrapper">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={currentPageData}
              margin={{ top: 20, right: 40, left: 20, bottom: 0 }}>
              <CartesianGrid
                strokeDasharray="2 4"
                stroke="#d1d5db"
                horizontal={true}
                vertical={true}
              />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={true}
                tick={<CustomXAxisTick />}
                height={60}
                interval={0}
              />
              <YAxis
                axisLine={false}
                tickLine={true}
                tick={{ fontSize: 13, fill: "#6b7280" }}
                width={40}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{
                  stroke: "#d1d5db",
                  strokeWidth: 1,
                  strokeDasharray: "4 4",
                }}
              />
              <Area
                type="monotone"
                dataKey="in_process"
                fill="rgba(245, 158, 11, 0.1)"
                stroke="none"
              />
              <Area
                type="monotone"
                dataKey="converted"
                fill="rgba(16, 185, 129, 0.1)"
                stroke="none"
              />
              <Line
                type="monotone"
                dataKey="total_leads"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={<CustomActiveDot />}
              />
              <Line
                type="monotone"
                dataKey="converted"
                stroke="#10b981"
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={<CustomActiveDot />}
              />
              <Line
                type="monotone"
                dataKey="in_process"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={<CustomActiveDot />}
              />
              <Line
                type="monotone"
                dataKey="lost"
                stroke="#ef4444"
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={<CustomActiveDot />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="no-data-message">
            <i className="ti ti-database-off"></i>
            <p>No data available!</p>
          </div>
        )}

        {chartData.length > 0 && (
          <div className="chart-controls">
            <div className="pagination-info">
              <span className="text-muted">
                Showing {startIndex + 1}-
                {Math.min(startIndex + itemsPerPage, chartData.length)} of{" "}
                {chartData.length} sources
              </span>
            </div>
            <div className="pagination-controls">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handlePrevPage}
                disabled={currentPage === 1}>
                <i className="ti ti-chevron-left" />
                Prev
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}>
                Next
                <i className="ti ti-chevron-right" />
              </button>
              {navigationStack.length > 0 && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => handleNavigateBack(0)}>
                  <i className="ti ti-refresh" />
                  Reset
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadSourcesChart;
